import { useEffect, useRef } from 'react'

import { Button } from '../button/button.js'
import { AlertTriangle, X } from '../icon/solar-icons.js'
import './portal-confirm-dialog.css'

export interface PortalConfirmDialogProps {
    /** Whether the dialog is visible */
    open: boolean
    /** Dialog title */
    title: string
    /** Descriptive message */
    message: string
    /** Label for the confirm button */
    confirmLabel: string
    /** Label for the cancel button */
    cancelLabel: string
    /** Visual style of the confirm button */
    variant?: 'danger' | 'primary'
    /** Called when the user confirms */
    onConfirm: () => void
    /** Called when the user cancels or closes */
    onCancel: () => void
}

export const PortalConfirmDialog = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel,
    variant = 'danger',
    onConfirm,
    onCancel,
}: PortalConfirmDialogProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const el = dialogRef.current
        if (!el) return

        if (open && !el.open) {
            el.showModal()
        } else if (!open && el.open) {
            el.close()
        }
    }, [open])

    // Close on backdrop click
    const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
        if (e.target === dialogRef.current) onCancel()
    }

    // Close on Escape
    const handleCancel = (e: React.SyntheticEvent) => {
        e.preventDefault()
        onCancel()
    }

    if (!open) return null

    return (
        <dialog
            ref={dialogRef}
            className="portal-confirm-dialog"
            onClick={handleBackdropClick}
            onCancel={handleCancel}
        >
            <div className="portal-confirm-dialog__panel">
                <div className="portal-confirm-dialog__header">
                    <div className="portal-confirm-dialog__icon-wrap">
                        <AlertTriangle size={20} />
                    </div>
                    <h3 className="portal-confirm-dialog__title">{title}</h3>
                    <Button
                        variant="tertiary"
                        size="small"
                        iconOnly
                        iconLeft={X}
                        text={cancelLabel}
                        onClick={onCancel}
                    />
                </div>
                <p className="portal-confirm-dialog__message">{message}</p>
                <div className="portal-confirm-dialog__actions">
                    <Button variant="tertiary" size="small" text={cancelLabel} onClick={onCancel} />
                    <Button
                        variant={variant === 'danger' ? 'alert' : 'primary'}
                        size="small"
                        text={confirmLabel}
                        onClick={onConfirm}
                        buttonProps={{ autoFocus: true }}
                    />
                </div>
            </div>
        </dialog>
    )
}
